import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { SiteConfig, Slide, GalleryItem, Review } from '../data/defaultConfig';

// ============================================================
// TYPES
// ============================================================

export interface FormSubmission {
  id: string;
  fullName: string;
  email: string;
  countryCode: string;
  whatsapp: string;
  dateNeeded: string;
  occasion: string;
  theme: string;
  servings: string;
  message: string;
  productCodes: string;
  method: 'email' | 'whatsapp';
  submittedAt: string;
}

// ============================================================
// IMAGE UPLOAD TO SUPABASE STORAGE
// ============================================================

/**
 * Upload a file to a Supabase storage bucket and return its public URL.
 * bucket: 'gallery-images' | 'slide-images'
 */
export const uploadImageToStorage = async (
  file: File,
  bucket: 'gallery-images' | 'slide-images'
): Promise<string | null> => {
  if (!isSupabaseConfigured || !supabase) return null;

  const ext = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from(bucket)
    .upload(fileName, file, { upsert: false });

  if (error) {
    console.error('Storage upload error:', error.message);
    return null;
  }

  const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
  return data.publicUrl;
};

// ============================================================
// LOAD CONFIG FROM SUPABASE
// ============================================================

export const loadConfigFromSupabase = async (): Promise<Partial<SiteConfig> | null> => {
  if (!isSupabaseConfigured || !supabase) return null;

  try {
    // Fetch all in parallel
    const [configRes, slidesRes, galleryRes, reviewsRes] = await Promise.all([
      supabase.from('site_config').select('key,value'),
      supabase.from('slides').select('*').order('sort_order', { ascending: true }),
      supabase.from('gallery_items').select('*').order('sort_order', { ascending: true }),
      supabase.from('reviews').select('*').order('created_at', { ascending: true }),
    ]);

    // If any table returned rows, we have a configured DB
    const hasData =
      (configRes.data && configRes.data.length > 0) ||
      (slidesRes.data && slidesRes.data.length > 0) ||
      (galleryRes.data && galleryRes.data.length > 0) ||
      (reviewsRes.data && reviewsRes.data.length > 0);

    if (!hasData) return null; // DB is empty — will use defaults

    // Map site_config key-value rows back to object
    const configMap: Record<string, string> = {};
    (configRes.data || []).forEach((row) => {
      configMap[row.key] = row.value;
    });

    // Map slides rows to Slide[]
    const slides: Slide[] = (slidesRes.data || []).map((row) => ({
      src: row.src,
      lazy: row.lazy,
      badge: { en: row.badge_en, id: row.badge_id },
      title: { en: row.title_en, id: row.title_id },
      subtitle: { en: row.subtitle_en, id: row.subtitle_id },
      ctaLabel: row.cta_label_en ? { en: row.cta_label_en, id: row.cta_label_id || '' } : undefined,
      ctaLink: row.cta_link || undefined,
      hidePrimaryCta: row.hide_primary_cta,
    }));

    // Map gallery rows to GalleryItem[]
    const galleryItems: GalleryItem[] = (galleryRes.data || []).map((row) => ({
      id: row.id,
      code: row.code,
      image: row.image,
      title: row.title,
      category: row.category,
      description: row.description || '',
    }));

    // Map review rows to Review[]
    const reviews: Review[] = (reviewsRes.data || []).map((row) => ({
      reviewId: row.review_id,
      displayName: row.display_name,
      profilePhotoUrl: row.profile_photo_url,
      starRating: row.star_rating,
      comment: row.comment,
      createTime: row.create_time,
    }));

    return {
      phoneNumberTony: configMap['phoneNumberTony'] || undefined,
      phoneNumberNila: configMap['phoneNumberNila'] || undefined,
      whatsappMessageTonyEn: configMap['whatsappMessageTonyEn'] || undefined,
      whatsappMessageTonyId: configMap['whatsappMessageTonyId'] || undefined,
      whatsappMessageNilaEn: configMap['whatsappMessageNilaEn'] || undefined,
      whatsappMessageNilaId: configMap['whatsappMessageNilaId'] || undefined,
      googleRating: configMap['googleRating'] ? parseFloat(configMap['googleRating']) : undefined,
      googleReviewCount: configMap['googleReviewCount'] ? parseInt(configMap['googleReviewCount']) : undefined,
      slides: slides.length > 0 ? slides : undefined,
      galleryItems: galleryItems.length > 0 ? galleryItems : undefined,
      reviews: reviews.length > 0 ? reviews : undefined,
    };
  } catch (err) {
    console.error('Failed to load config from Supabase:', err);
    return null;
  }
};

// ============================================================
// SAVE FULL CONFIG TO SUPABASE
// ============================================================

export const saveConfigToSupabase = async (config: SiteConfig): Promise<boolean> => {
  if (!isSupabaseConfigured || !supabase) return false;

  try {
    // 1. Upsert site_config key-value pairs
    const configRows = [
      { key: 'phoneNumberTony', value: config.phoneNumberTony },
      { key: 'phoneNumberNila', value: config.phoneNumberNila },
      { key: 'whatsappMessageTonyEn', value: config.whatsappMessageTonyEn },
      { key: 'whatsappMessageTonyId', value: config.whatsappMessageTonyId },
      { key: 'whatsappMessageNilaEn', value: config.whatsappMessageNilaEn },
      { key: 'whatsappMessageNilaId', value: config.whatsappMessageNilaId },
      { key: 'googleRating', value: String(config.googleRating) },
      { key: 'googleReviewCount', value: String(config.googleReviewCount) },
    ];
    const { error: err1 } = await supabase.from('site_config').upsert(configRows, { onConflict: 'key' });
    if (err1) {
      console.error('Supabase site_config upsert error:', err1);
      return false;
    }

    // 2. Replace slides: delete all then re-insert with sort_order
    const { error: err2 } = await supabase.from('slides').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    if (err2) {
      console.error('Supabase slides delete error:', err2);
      return false;
    }

    if (config.slides.length > 0) {
      const slideRows = config.slides.map((s, idx) => ({
        src: s.src,
        lazy: s.lazy !== false,
        badge_en: s.badge.en,
        badge_id: s.badge.id,
        title_en: s.title.en,
        title_id: s.title.id,
        subtitle_en: s.subtitle.en,
        subtitle_id: s.subtitle.id,
        cta_label_en: s.ctaLabel?.en || null,
        cta_label_id: s.ctaLabel?.id || null,
        cta_link: s.ctaLink || null,
        hide_primary_cta: !!s.hidePrimaryCta,
        sort_order: idx,
      }));
      const { error: err3 } = await supabase.from('slides').insert(slideRows);
      if (err3) {
        console.error('Supabase slides insert error:', err3);
        return false;
      }
    }

    // 3. Replace gallery items: delete all then re-insert
    const { error: err4 } = await supabase.from('gallery_items').delete().neq('id', '__never__');
    if (err4) {
      console.error('Supabase gallery_items delete error:', err4);
      return false;
    }

    if (config.galleryItems.length > 0) {
      const batchSize = 100;
      for (let i = 0; i < config.galleryItems.length; i += batchSize) {
        const batch = config.galleryItems.slice(i, i + batchSize).map((item, idx) => ({
          id: item.id,
          code: item.code,
          image: item.image,
          title: item.title,
          category: item.category,
          description: item.description || '',
          sort_order: i + idx,
        }));
        const { error: err5 } = await supabase.from('gallery_items').insert(batch);
        if (err5) {
          console.error('Supabase gallery_items insert error:', err5);
          return false;
        }
      }
    }

    // 4. Replace reviews: delete all then re-insert
    const { error: err6 } = await supabase.from('reviews').delete().neq('review_id', '__never__');
    if (err6) {
      console.error('Supabase reviews delete error:', err6);
      return false;
    }

    if (config.reviews.length > 0) {
      const reviewRows = config.reviews.map((r) => ({
        review_id: r.reviewId,
        display_name: r.displayName,
        profile_photo_url: r.profilePhotoUrl,
        star_rating: r.starRating,
        comment: r.comment,
        create_time: r.createTime,
      }));
      const { error: err7 } = await supabase.from('reviews').insert(reviewRows);
      if (err7) {
        console.error('Supabase reviews insert error:', err7);
        return false;
      }
    }

    return true;
  } catch (err) {
    console.error('Failed to save config to Supabase:', err);
    return false;
  }
};

// ============================================================
// FORM SUBMISSIONS
// ============================================================

export const saveSubmissionToSupabase = async (submission: FormSubmission): Promise<boolean> => {
  if (!isSupabaseConfigured || !supabase) return false;

  try {
    const { error } = await supabase.from('form_submissions').insert({
      id: submission.id,
      full_name: submission.fullName,
      email: submission.email,
      country_code: submission.countryCode,
      whatsapp: submission.whatsapp,
      date_needed: submission.dateNeeded,
      occasion: submission.occasion,
      theme: submission.theme,
      servings: submission.servings,
      message: submission.message,
      product_codes: submission.productCodes,
      method: submission.method,
      submitted_at: submission.submittedAt,
    });

    if (error) {
      console.error('Failed to save submission to Supabase:', error.message);
      return false;
    }
    return true;
  } catch (err) {
    console.error('Failed to save submission:', err);
    return false;
  }
};

export const loadSubmissionsFromSupabase = async (): Promise<FormSubmission[]> => {
  if (!isSupabaseConfigured || !supabase) return [];

  try {
    const { data, error } = await supabase
      .from('form_submissions')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error || !data) return [];

    return data.map((row) => ({
      id: row.id,
      fullName: row.full_name,
      email: row.email,
      countryCode: row.country_code,
      whatsapp: row.whatsapp,
      dateNeeded: row.date_needed,
      occasion: row.occasion,
      theme: row.theme,
      servings: row.servings,
      message: row.message,
      productCodes: row.product_codes,
      method: row.method,
      submittedAt: row.submitted_at,
    }));
  } catch (err) {
    console.error('Failed to load submissions:', err);
    return [];
  }
};

export const deleteSubmissionFromSupabase = async (id: string): Promise<boolean> => {
  if (!isSupabaseConfigured || !supabase) return false;

  try {
    const { error } = await supabase.from('form_submissions').delete().eq('id', id);
    return !error;
  } catch {
    return false;
  }
};

export const clearAllSubmissionsFromSupabase = async (): Promise<boolean> => {
  if (!isSupabaseConfigured || !supabase) return false;

  try {
    const { error } = await supabase.from('form_submissions').delete().neq('id', '__never__');
    return !error;
  } catch {
    return false;
  }
};
